<template>
    <div class="dashboard-container">
        <button class="menu-toggle" @click="toggleMenu">
            <span class="menu-icon"></span>
        </button>

        <nav class="dashboard-nav" :class="{ 'menu-open': isMenuOpen }">
            <div class="user-info">
                <img :src="userAvatarUrl" alt="Avatar" class="user-avatar" @error="$event.target.src='https://cdn.discordapp.com/embed/avatars/0.png'">
                <p>{{ userName }}</p>
                <div v-if="moderatedSubscriptions.length" class="moderator-impersonation">
                    <label class="moderator-label" for="moderator-subscription-select">Mode modérateur</label>
                    <select
                        id="moderator-subscription-select"
                        v-model="selectedModeratedSubscriptionId"
                        @change="onModeratedSubscriptionChange"
                        class="moderator-select"
                        :disabled="moderatorSubscriptionsLoading"
                    >
                        <option value="">Mon compte</option>
                        <option
                            v-for="subscription in moderatedSubscriptions"
                            :key="subscription.id"
                            :value="subscription.id"
                        >
                            {{ subscription.twitchName }}
                        </option>
                    </select>
                    <p v-if="moderatorSubscriptionsLoading" class="moderator-hint">Chargement des accès...</p>
                    <p v-else class="moderator-hint">Choisis une souscription à gérer.</p>
                </div>

                <div v-if="isAdmin" class="admin-impersonation">
                    <div class="search-container">
                        <input
                            type="text"
                            v-model="searchQuery"
                            @input="searchUsers"
                            placeholder="Rechercher un utilisateur..."
                            class="user-search"
                        />
                        <div v-if="searchResults.length && showResults" class="search-results">
                            <div
                                v-for="user in searchResults"
                                :key="user.id"
                                @click="impersonateUser(user)"
                                class="search-result-item"
                            >
                                <img
                                    :src="user.discordAvatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'"
                                    :alt="user.twitchName"
                                    class="mini-avatar"
                                    @error="$event.target.src='https://cdn.discordapp.com/embed/avatars/0.png'"
                                />
                                <span>{{ user.twitchName }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="impersonatedUser" class="impersonation-active">
                    <p>Vue en tant que: {{ impersonatedUser.twitchName }}</p>
                    <button @click="stopImpersonation" class="stop-impersonation">
                        Revenir à mon compte
                    </button>
                </div>
            </div>
            
            <ul>
                <li :class="{ active: route.name === 'DashboardGeneral' }">
                    <router-link to="/dashboard/general" class="nav-link">
                        <span>General</span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardPremium' }">
                    <router-link to="/dashboard/premium" class="nav-link">
                        <span>Premium</span>
                        <span class="status-dot" :class="{ active: premiumActive }" :title="premiumActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardCustomBot' }">
                    <router-link to="/dashboard/custom-bot" class="nav-link">
                        <span>Bot Twitch perso</span>
                        <span class="status-dot" :class="{ active: customBotActive }" :title="customBotActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardRolePlay' || route.name === 'DashboardRolePlayTool' }">
                    <router-link to="/dashboard/roleplay" class="nav-link">
                        <span>GTA RP</span>
                        <span class="status-dot" :class="{ active: roleplayActive }" :title="roleplayActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li v-if="roleplayActive" :class="{ active: route.name === 'DashboardRolePlayTool' }" class="submenu-item">
                    <router-link to="/dashboard/roleplay/tools" class="nav-link submenu-link">
                        <span>↳ Outil Check RP</span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardSafeSpace' }">
                    <router-link to="/dashboard/safespace" class="nav-link">
                        <span>SafeSpace</span>
                        <span class="status-dot" :class="{ active: safespaceActive }" :title="safespaceActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardModeration' }">
                    <router-link to="/dashboard/moderation" class="nav-link">
                        <span>Modération</span>
                        <span class="status-dot" :class="{ active: moderationActive }" :title="moderationActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardVip' }">
                    <router-link to="/dashboard/vip" class="nav-link">
                        <span>VIP</span>
                        <span class="status-dot" :class="{ active: vipActive }" :title="vipActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li :class="{ active: route.name === 'DashboardIA' }">
                    <router-link to="/dashboard/ia" class="nav-link">
                        <span>Intelligence Artificielle</span>
                        <span class="status-dot" :class="{ active: iaActive }" :title="iaActive ? 'Activé' : 'Désactivé'"></span>
                    </router-link>
                </li>
                <li v-if="premiumActive || premiumTesting || isAdmin" :class="{ active: route.name === 'DashboardReports' }">
                    <router-link to="/dashboard/reports" class="nav-link">
                        <span>Rapports</span>
                    </router-link>
                </li>
                <li v-if="isAdmin" :class="{ active: route.name === 'DashboardAdmin' }">
                    <router-link to="/dashboard/admin" class="nav-link">
                        <span>Admin</span>
                    </router-link>
                </li>
            </ul>
            
            <div class="nav-buttons">
                <button v-if="premiumActive || premiumTesting || isAdmin" class="logs-button" @click="openLiveLogs">
                    📡 Logs en Direct
                </button>
                <div class="help-button" :class="{ 'has-tips': hasTips, 'inactive': !hasTips && !showHelpButtonAlways }" @click="openBuddiAssistant" v-if="hasTips || showHelpButtonAlways">
                    <div v-if="hasTips" class="notification-dot"></div>
                    <img src="../assets/buddi-avatar.png" alt="Buddi" class="buddi-icon" />
                    <span v-if="hasTips">Conseil disponible !</span>
                    <span v-else>Besoin d'aide ?</span>
                </div>
                <button class="logout-button" @click="logout">Se déconnecter</button>
            </div>
        </nav>

        <div class="dashboard-content">
            <router-view v-slot="{ Component }">
                <component :is="Component" @premium-updated="handlePremiumUpdate" />
            </router-view>
        </div>

        <div class="menu-overlay" v-if="isMenuOpen" @click="toggleMenu"></div>
    </div>
</template>

<script>
import { notifications } from '../store/notifications'
import { impersonationStore } from '../store/impersonation'
import { api } from '../services/api'
import { authService } from '../services/auth'
import { subscriptionService } from '../services/subscription'
import { useSubscriptionData } from '../composables/useSubscriptionData'
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

export default {
    name: 'Dashboard',
    components: {
    },
    setup() {
        const { fetchSubscriptionData } = useSubscriptionData()
        const route = useRoute()
        const isMenuOpen = ref(false)
        const isAdmin = ref(false)
        const searchQuery = ref('')
        const searchResults = ref([])
        const showResults = ref(false)
        const moderatedSubscriptions = ref([])
        const selectedModeratedSubscriptionId = ref('')
        const moderatorSubscriptionsLoading = ref(false)
        const userName = ref('')
        const userAvatarUrl = ref('')
        const hasTips = ref(false) // Indique s'il y a des conseils disponibles
        const showHelpButtonAlways = ref(true) // Définit si le bouton d'aide est toujours visible même sans conseils
        
        // Récupérer les données de souscription une seule fois au niveau du setup
        const { subscriptionData } = useSubscriptionData();

        // Helpers to read module activation
        const getDataRoot = () => subscriptionData.value?.data || subscriptionData.value || null
        const isModuleActive = (type) => {
            const data = getDataRoot()
            const list = data?.botModules || data?.BotModules
            if (!Array.isArray(list)) return false
            const mod = list.find(m => m.moduleType === type || m.ModuleType === type)
            return !!(mod && (mod.isActive || mod.IsActive))
        }

        const premiumActive = computed(() => {
            const data = getDataRoot()
            if (!data) return false
            if (data.isVIP === true || data.IsVIP === true) return true
            const exp = data.premiumExpirationDate || data.PremiumExpirationDate
            return exp ? new Date(exp) > new Date() : false
        })
        const premiumTesting = computed(() => {
            const data = getDataRoot()
            if (!data) return false
            return data.premiumTesting === true || data.PremiumTesting === true
        })
        const roleplayActive = computed(() => isModuleActive(101))
        const safespaceActive = computed(() => isModuleActive(102))
        const moderationActive = computed(() => isModuleActive(104))
        const vipActive = computed(() => isModuleActive(100))
        const iaActive = computed(() => isModuleActive(103))
        const customBotActive = computed(() => getDataRoot()?.customBot?.isEnabled === true)
        
        // Méthode pour vérifier s'il y a des conseils disponibles
        const checkForTips = async () => {
            try {
                
                // Si les données ne sont pas encore chargées, on ne peut pas vérifier maintenant
                if (!subscriptionData.value) {
                    return; // Sortir de la fonction car on ne peut pas vérifier sans données
                }
                
                // Attendre un peu pour s'assurer que les données sont chargées
                setTimeout(() => {
                    // Vérifier si les données sont disponibles
                    if (subscriptionData.value && subscriptionData.value.data) {
                        const userData = subscriptionData.value.data;                        
                        
                        // Vérification plus stricte pour s'assurer que la condition fonctionne correctement
                        // Convertir en string pour éviter les problèmes de type
                        // Support both camelCase and PascalCase for backward compatibility
                        const discordChannelIdStr = String(userData.discordChannelId || userData.DiscordChannelId || '');
                        
                        const isEmpty = !discordChannelIdStr || discordChannelIdStr === '';
                        const isZero = discordChannelIdStr === '0' || discordChannelIdStr === '0.0';
                        const needsSetup = isEmpty || isZero;
                        
                        // Vérifier si le DiscordChannelId est configuré
                        if (needsSetup) {
                            hasTips.value = true;
                        } else {
                            hasTips.value = false;
                        }                        
                    }
                }, 3000); // Attendre 3 secondes pour que les données soient chargées
            } catch (error) {
                console.error('Erreur lors de la vérification des conseils:', error);
            }
        }
        
        const openBuddiAssistant = async () => {
            // Vérifier si le service Buddi est disponible
            if (window.$buddiAssistant) {
                
                // Utiliser les données de souscription déjà récupérées
                let needsDiscordSetup = false;
                let userData = null;
                
                if (subscriptionData.value && subscriptionData.value.data) {
                    userData = subscriptionData.value.data;                    
                    
                    // Récupérer l'ID du canal Discord en tant que chaîne de caractères
                    // Important: Les IDs Discord doivent toujours être traités comme des chaînes
                    // car ils dépassent la limite de précision sûre de JavaScript (2^53 - 1)
                    let discordChannelIdStr;
                    
                    // Support both camelCase and PascalCase for backward compatibility
                    const discordChannelId = userData.discordChannelId || userData.DiscordChannelId;
                    
                    // Vérifier si l'ID est déjà une chaîne
                    if (typeof discordChannelId === 'string') {
                        discordChannelIdStr = discordChannelId;
                    } 
                    // Si c'est un nombre, le convertir en chaîne avec précaution
                    else if (typeof discordChannelId === 'number') {
                        // Utiliser JSON.stringify pour éviter les problèmes d'arrondi
                        discordChannelIdStr = JSON.stringify(discordChannelId).replace(/"/g, '');
                    } 
                    // Valeur par défaut
                    else {
                        discordChannelIdStr = '';
                    }
                    
                    //console.log('discordChannelIdStr:', discordChannelIdStr);
                    
                    const isEmpty = !discordChannelIdStr || discordChannelIdStr === '';
                    const isZero = discordChannelIdStr === '0' || discordChannelIdStr === '0.0';
                    needsDiscordSetup = isEmpty || isZero;
                }
                
                // Réinitialiser l'état des conseils après ouverture
                hasTips.value = false;                

                // Configurer et afficher l'assistant avec des messages différents selon le cas
                if (needsDiscordSetup) {
                    // Message spécifique pour la configuration du canal Discord
                    window.$buddiAssistant.show({
                        title: 'Configuration nécessaire',
                        messages: [
                            'Bonjour ! J\'ai remarqué que vous n\'avez pas encore configuré de canal Discord pour BuddiBot.',
                            'Pour profiter pleinement des fonctionnalités de BuddiBot, vous devez créer un canal dans votre serveur Discord et inviter BuddiBot.'
                        ],
                        options: [
                            {
                                text: 'Comment configurer mon canal Discord ?',
                                responses: [
                                    'Voici les étapes pour configurer votre canal Discord :',
                                    '1. Créez un nouveau canal dans votre serveur Discord',
                                    '2. Invitez BuddiBot à votre serveur en utilisant le lien d\'invitation disponible sur notre site',
                                    '3. Donnez à BuddiBot les permissions nécessaires pour lire et envoyer des messages dans ce canal',
                                    '4. **IMPORTANT** : Tapez "@buddibot register" dans le canal Discord que vous avez créé',
                                    '5. BuddiBot détectera automatiquement l\'ID du canal et configurera votre compte'
                                ],
                                nextOptions: [
                                    { 
                                        text: 'Où trouver l\'ID du canal ?', 
                                        responses: [
                                            'Pour trouver l\'ID du canal Discord :',
                                            '1. Activez le "Mode développeur" dans les paramètres Discord (Paramètres > Avancé > Mode développeur)',
                                            '2. Faites un clic droit sur le canal',
                                            '3. Sélectionnez "Copier l\'identifiant"',
                                            'Cet ID est une série de chiffres unique qui identifie votre canal.'
                                        ] 
                                    },
                                    { 
                                        text: 'Comment fonctionne la commande register ?', 
                                        responses: [
                                            'La commande "@buddibot register" est essentielle pour la configuration :',
                                            '1. Elle permet à BuddiBot de détecter automatiquement l\'ID du canal',
                                            '2. BuddiBot vérifie qu\'il a bien les permissions nécessaires dans ce canal',
                                            '3. L\'ID est automatiquement enregistré dans votre compte, sans que vous ayez à l\'entrer manuellement',
                                            '4. BuddiBot confirmera l\'enregistrement par un message dans le canal'
                                        ] 
                                    },
                                    {
                                        text: 'Pourquoi rien ne se passe quand je tape la commande ?',
                                        responses: [
                                            'Si rien ne se passe lorsque vous tapez "@buddibot register" dans le canal, cela peut être dû à plusieurs raisons :',
                                            '1. BuddiBot n\'a pas les permissions nécessaires pour lire ou écrire dans ce canal',
                                            '2. BuddiBot n\'a pas été correctement invité sur votre serveur Discord',
                                            '3. Le canal est restreint et BuddiBot n\'y a pas accès',
                                            '4. Vous avez peut-être mentionné le rôle BuddiBot au lieu du bot lui-même',
                                            'Si vous pensez que c\'est un problème de mention, essayez de copier-coller cette commande exacte :',
                                            '<@911047298262376460> register',
                                            'Cette commande mentionne directement BuddiBot par son ID plutôt que par son nom, ce qui garantit que c\'est bien le bot qui est mentionné et non un rôle portant le même nom.',
                                            'Vérifiez également les permissions du canal et assurez-vous que BuddiBot a accès au canal avec les droits de lecture et d\'envoi de messages.'
                                        ]
                                    }
                                ]
                            },
                            {
                                text: 'Pourquoi est-ce important ?',
                                responses: [
                                    'Configurer un canal Discord est essentiel car :',
                                    '- BuddiBot utilise ce canal pour envoyer des notifications importantes',
                                    '- Les fonctionnalités comme SafeSpace et GTA RP nécessitent un canal pour communiquer',
                                    '- Vous pourrez recevoir des alertes et des rapports directement dans votre serveur Discord',
                                    'Sans cette configuration, certaines fonctionnalités de BuddiBot ne fonctionneront pas correctement.',
                                    '',
                                    '**IMPORTANT** : La commande "@buddibot register" dans le canal Discord est l\'unique étape nécessaire pour configurer l\'ID. BuddiBot détectera automatiquement l\'ID du canal et l\'enregistrera dans votre compte. Vous n\'avez pas besoin d\'entrer manuellement cet ID dans le dashboard.'
                                ]
                            }
                        ]
                    });
                } else {
                    // Message standard pour l'assistance générale
                    window.$buddiAssistant.show({
                        title: 'Assistance BuddiBot',
                        messages: [
                            'Bonjour ! Je suis Buddi, votre assistant personnel. Comment puis-je vous aider aujourd\'hui ?',
                            'Voici quelques sujets sur lesquels je peux vous aider :'
                        ],
                        options: [
                            {
                                text: 'J\'ai un problème technique',
                                responses: ['Je suis désolé pour ce problème. que se passe-t-il?'],
                                nextOptions: [
                                    { text: 'Le bot ne répond pas', responses: ['Si le bot ne répond pas, il est possible qu\'il soit hors ligne ou qu\'il rencontre des difficultés techniques. Vérifiez que le bot est bien enregistré sur votre serveur Discord et que les permissions sont correctement configurées. Si le problème persiste, contactez notre support pour plus d\'informations...'] },
                                    { text: 'j\'ai besoin d\'aide', responses: ['Pour une aide personnalisée, contactez DoZz sur discord: dozz.lancien'] }
                                ]
                            }
                        ]
                    });
                }
            } else {
                console.error('Service Buddi non disponible')
            }
        }

        const impersonatedUserComputed = computed(() => impersonationStore.currentUser)


        const toggleMenu = () => {
            isMenuOpen.value = !isMenuOpen.value
        }

        const logout = () => {
            authService.logout()
        }

        const openLiveLogs = () => {
            const logsUrl = `${window.location.origin}/logs/live`
            window.open(logsUrl, '_blank')
        }

        // Close the mobile menu whenever the route changes
        watch(() => route.fullPath, () => {
            if (isMenuOpen.value) isMenuOpen.value = false
        })

        const checkAdminRights = async () => {
            try {
                const response = await api.get('/subscriptions/isAdmin')
                const flag = response?.data?.data?.isAdmin === true
                isAdmin.value = flag
                localStorage.setItem('is_admin', flag ? 'true' : 'false')
            } catch (error) {
                console.error('Error checking admin rights:', error)
                isAdmin.value = false
                localStorage.setItem('is_admin', 'false')
            }
        }

        const loadModeratedSubscriptions = async () => {
            moderatorSubscriptionsLoading.value = true
            try {
                const response = await subscriptionService.getModeratorSubscriptions()
                moderatedSubscriptions.value = response?.data?.data || []
                if (impersonationStore.currentUser?.id) {
                    selectedModeratedSubscriptionId.value = String(impersonationStore.currentUser.id)
                }
            } catch (error) {
                console.error('Error loading moderated subscriptions:', error)
                moderatedSubscriptions.value = []
            } finally {
                moderatorSubscriptionsLoading.value = false
            }
        }

        const searchUsers = async () => {
            if (!searchQuery.value) {
                searchResults.value = []
                showResults.value = false
                return
            }

            try {
                const response = await api.get(`/subscriptions/search?keyword=${searchQuery.value}`)
                searchResults.value = response?.data?.users || []
                showResults.value = true
            } catch (error) {
                console.error('Error searching users:', error)
                notifications.add({
                    type: 'error',
                    message: 'Erreur lors de la recherche d\'utilisateurs',
                    timeout: 5000
                })
            }
        }

        const impersonateUser = async (user) => {
            try {
                // Instead of a dedicated endpoint, we'll just call GetSubInformations with the impersonateId
                const response = await api.get(`/subscriptions?impersonateId=${user.id}`)
                if (response.data.success) {
                    await impersonationStore.setCurrentUser(user)
                    showResults.value = false
                    searchQuery.value = ''
                    searchResults.value = []
                    
                    notifications.add({
                        type: 'success',
                        message: `Vue changée pour ${user.twitchName}`,
                        timeout: 5000
                    })

                    // Recharger les données de souscription
                    await fetchSubscriptionData()
                }
            } catch (error) {
                console.error('Error impersonating user:', error)
                notifications.add({
                    type: 'error',
                    message: 'Erreur lors du changement d\'utilisateur',
                    timeout: 5000
                })
            }
        }

        const stopImpersonation = async () => {
            try {
                // Call GetSubInformations without impersonateId to stop impersonation
                const response = await api.get('/subscriptions')
                if (response.data.success) {
                    await impersonationStore.setCurrentUser(null)
                    selectedModeratedSubscriptionId.value = ''
                    notifications.add({
                        type: 'success',
                        message: 'Retour à votre compte',
                        timeout: 5000
                    })
                    
                    // Recharger les données de souscription
                    await fetchSubscriptionData()
                }
            } catch (error) {
                console.error('Error stopping impersonation:', error)
            }
        }

        const onModeratedSubscriptionChange = async () => {
            if (!selectedModeratedSubscriptionId.value) {
                await stopImpersonation()
                return
            }

            const selected = moderatedSubscriptions.value.find(
                s => String(s.id) === String(selectedModeratedSubscriptionId.value)
            )

            if (!selected) {
                return
            }

            await impersonateUser(selected)
        }

        const loadUserData = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem('discord_user'))
                if (userData) {
                    userName.value = userData.global_name || userData.username
                    if (userData.avatar) {
                        // Utiliser le format WebP pour une meilleure performance et qualité
                        // Taille 128x128 pour un bon compromis qualité/performance
                        userAvatarUrl.value = `https://cdn.discordapp.com/avatars/${userData.id}/${userData.avatar}.webp?size=128`
                    } else {
                        // Utiliser l'avatar par défaut basé sur le discriminator
                        const defaultIndex = userData.discriminator === "0" ? Math.floor(Math.random() * 6) : parseInt(userData.discriminator) % 5
                        userAvatarUrl.value = `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png`
                    }
                }
                
                await fetchSubscriptionData()
                await checkAdminRights()
                await loadModeratedSubscriptions()
            } catch (error) {
                console.error('Error in loadUserData:', error)
                userName.value = 'Unknown User'
                userAvatarUrl.value = 'https://cdn.discordapp.com/embed/avatars/0.png'
            }
        }

        const handlePremiumUpdate = async () => {
            await fetchSubscriptionData()
        }

        // Surveiller les changements dans les données de souscription
        watch(subscriptionData, (newData) => {
            if (newData) {
                checkForTips();
            }
        });
        
        onMounted(async () => {
            await loadUserData();
            // La vérification des conseils sera déclenchée automatiquement par le watcher
            // lorsque les données de souscription seront chargées
        })

        return {
            route,
            isMenuOpen,
            isAdmin,
            hasTips,
            showHelpButtonAlways,
            toggleMenu,
            logout,
            openLiveLogs,
            searchQuery,
            searchResults,
            showResults,
            searchUsers,
            moderatedSubscriptions,
            selectedModeratedSubscriptionId,
            moderatorSubscriptionsLoading,
            onModeratedSubscriptionChange,
            impersonateUser,
            impersonatedUser: impersonatedUserComputed,
            stopImpersonation,
            userName,
            userAvatarUrl,
            handlePremiumUpdate,
            openBuddiAssistant,
            premiumActive,
            premiumTesting,
            roleplayActive,
            safespaceActive,
            moderationActive,
            vipActive,
            iaActive,
            customBotActive
        }
    }
}
</script>

<style scoped>
.dashboard-container {
    display: flex;
    height: 100vh;
    overflow: hidden;
    position: relative;
}

.menu-toggle {
    display: none;
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 1000;
    background: #004B55;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 5px;
    cursor: pointer;
    padding: 0;
}

.menu-icon {
    position: relative;
    display: block;
    width: 20px;
    height: 2px;
    background: white;
    margin: 0 auto;
}

.menu-icon::before,
.menu-icon::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background: white;
    left: 0;
}

.menu-icon::before {
    top: -6px;
}

.menu-icon::after {
    bottom: -6px;
}

.dashboard-nav {
    width: 250px;
    background-color: #004B55;
    color: white;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 200;
    transition: transform 0.3s ease;
}

.dashboard-content {
    flex-grow: 1;
    padding: 20px;
    margin-left: 250px;
    height: 100vh;
    overflow-y: auto;
    transition: margin-left 0.3s ease;
}

.menu-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
}/* Styles mobiles */
@media (max-width: 768px) {
    .menu-toggle {
        display: block;
    }

    .menu-overlay {
        display: block;
    }

    .dashboard-nav {
        transform: translateX(-100%);
    }

    .dashboard-nav.menu-open {
        transform: translateX(0);
    }

    .dashboard-content {
        margin-left: 0;
        padding-top: 80px;
    }
}

.user-info {
    padding: 20px;
    text-align: center;
    background-color: #003C45;
    border-bottom: 1px solid #006D7F;
}

.user-info p {
    margin: 0;
    font-size: 18px;
    font-weight: bold;
}

.user-avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    border: 2px solid #006D7F;
}

.dashboard-nav ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
    flex-grow: 1;
    overflow-y: auto;
}

.dashboard-nav li {
    padding: 0;
    border-bottom: 1px solid #006D7F;
}

.dashboard-nav li a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 20px;
    color: inherit;
    text-decoration: none;
    cursor: pointer;
}

.status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #A0AEC0; /* lighter neutral for visibility */
    border: 1px solid rgba(0, 0, 0, 0.5); /* contrast on teal bg */
    flex-shrink: 0;
}

.status-dot.active {
    background-color: var(--primary-color); /* brand yellow for active */
    box-shadow: 0 0 6px var(--primary-color);
}

.dashboard-nav li.active {
    background-color: #006D7F;
}

.dashboard-nav li.submenu-item {
    border-bottom: 1px solid #005f6e;
}

.dashboard-nav li.submenu-item a.submenu-link {
    padding: 12px 20px 12px 34px;
    font-size: 0.92rem;
    opacity: 0.95;
}

.logout-button {
    padding: 20px;
    background-color: #004B55;
    color: white;
    border: none;
    cursor: pointer;
    font-size: 14px;
    width: 100%;
}

.logout-button:hover {
    background-color: #006D7F;
}

.admin-impersonation {
    margin-top: 1rem;
    padding: 0.5rem;
}

.moderator-impersonation {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.moderator-label {
    font-size: 0.8rem;
    color: #cbd5e0;
    text-align: left;
}

.moderator-select {
    width: 100%;
    padding: 0.45rem 0.5rem;
    border: 1px solid #2d3748;
    border-radius: 4px;
    background-color: #1a202c;
    color: #fff;
    font-size: 0.9rem;
}

.moderator-hint {
    margin: 0;
    font-size: 0.75rem;
    color: #a0aec0;
    text-align: left;
}

.search-container {
    position: relative;
    width: 100%;
}

.user-search {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--color-border);
    border-radius: 4px;
    font-size: 0.9rem;
    background-color: var(--color-background);
    color: var(--color-text);
}

.search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    margin-top: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
}

.search-result-item {
    padding: 0.8rem;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--color-text);
    transition: background-color 0.2s;
    background-color: var(--color-background);
}

.search-result-item:hover {
    background-color: var(--color-background-hover);
}

.mini-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: 0.8rem;
    object-fit: cover;
    background-color: var(--color-background-soft);
}

.impersonation-active {
    margin-top: 0.8rem;
    padding: 0.8rem;
    background-color: var(--color-background-soft);
    border-radius: 4px;
    font-size: 0.9rem;
    border: 1px solid var(--color-border);
}

.impersonation-active p {
    color: var(--color-text);
    margin-bottom: 0.5rem;
}

.stop-impersonation {
    background-color: var(--color-danger);
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.2s;
    width: 100%;
}

.stop-impersonation:hover {
    background-color: var(--color-danger-hover);
}

.nav-buttons {
    display: flex;
    flex-direction: column;
    width: 100%;
}

/* Sidebar-specific buttons (override shared button styles) */
.logs-button {
    padding: 15px 20px;
    background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
    color: white;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    width: 100%;
    border-radius: 4px;
    margin-bottom: 10px;
    transition: all 0.3s ease;
}

.logs-button:hover {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
}

.logs-button:active {
    transform: translateY(0);
}

.help-button {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 15px;
    background-color: #1A202C;
    border: 1px solid #2D3748;
    border-radius: 8px;
    color: #A0AEC0;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.help-button:hover {
    background-color: #2D3748;
    color: white;
}

/* Style pour le bouton lorsqu'il y a des conseils disponibles */
.help-button.has-tips {
    background-color: #FF8C00;
    color: white;
    animation: pulse 2s infinite;
    border-color: #FF8C00;
}

.help-button.inactive {
    opacity: 0.7;
}

.help-button.inactive:hover {
    opacity: 1;
}

.help-button.has-tips:hover {
    background-color: #d99417;
    animation: none;
}

.help-button.inactive {
    opacity: 0.7;
}

.notification-dot {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 10px;
    height: 10px;
    background-color: #ff4757;
    border-radius: 50%;
    animation: blink 1.5s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.03);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes blink {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}

.buddi-icon {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    object-fit: cover;
}

:root {
    --color-background: #1a1a1a;
    --color-background-soft: #242424;
    --color-background-hover: #2c2c2c;
    --color-border: #363636;
    --color-text: #ffffff;
    --color-danger: #dc3545;
    --color-danger-hover: #c82333;
}
</style>